import BaseModal from '@/components/BaseModal';
import ConditionBanner from '@/components/ConditionBanner';
import ExcludeRuleCondition from '@/components/ExcludeRuleCondition';
import ModalItemCondition from '@/components/ModalItemCondition';
import RegularText from '@/components/RegularText';
import { AppliedItemTypes, BREAKPOINT, ItemConditionEnum } from '@/constants/enum';
import { checkConditionType } from '@/helpers';
import checkoutRuleSlice, {
  checkoutRuleSettingsSelector,
  currentOrderItemValueSelector,
  ICheckoutRuleErrorField,
} from '@/redux/slice/checkoutRule.slice';
import sessionSlice, { isShowBannerSelector } from '@/redux/slice/session.slice';
import { IConditionValue, IItemCondition, ILocationCondition } from '@/types/newSetup.type';
import { Button } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

interface IItemToRemove {
  conditionType: 'checkoutItemConditions' | 'checkoutLocationConditions';
  condition: IItemCondition['type'] | ILocationCondition['type'] | null;
}

export interface IDefaultOpenModalAddCondition {
  product: boolean;
  collection: boolean;
  vendor: boolean;
  exception: boolean;
}

const Step2 = () => {
  const dispatch = useDispatch();
  const state = useSelector(checkoutRuleSettingsSelector);
  const currentOrderItemValue = useSelector(currentOrderItemValueSelector);
  const { isShowBannerConditionCheckout } = useSelector(isShowBannerSelector);

  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const [isOpenModalItemCondition, setIsOpenModalItemCondition] = useState(false);
  const [isOpenModalConfirmRemoveCondition, setIsOpenModalConfirmRemoveCondition] = useState(false);
  const [trackRemove, setTrackRemove] = useState(false);
  const [selectedItemToRemove, setSelectedItemToRemove] = useState<IItemToRemove>({
    conditionType: 'checkoutItemConditions',
    condition: null,
  });

  const isInCaseInclude = useMemo(() => {
    return checkConditionType(state.checkoutItemConditions, AppliedItemTypes.Include);
  }, [state.checkoutItemConditions]);

  const isInCaseExclude = useMemo(() => {
    return checkConditionType(state.checkoutItemConditions, AppliedItemTypes.Exclude);
  }, [state.checkoutItemConditions]);

  const handleChangeCondition = useCallback(
    (conditionType: 'checkoutItemConditions' | 'checkoutLocationConditions') =>
      (key: IItemCondition['type'] | ILocationCondition['type']) =>
      (value: IConditionValue[] | null) => {
        let conditions = [...state[conditionType]];
        if (value) {
          let oldCondition = conditions.find((item) => item.type === key);
          if (oldCondition) {
            oldCondition = { ...oldCondition, value, order: oldCondition?.order ?? currentOrderItemValue };
            const indexOfOldCondition = conditions.findIndex((item) => item.type === key);
            conditions.splice(indexOfOldCondition, 1, oldCondition);
          } else {
            conditions.push({ type: key, value, order: currentOrderItemValue });
          }
          dispatch(checkoutRuleSlice.actions.handleAddCurrentCount());
        } else {
          conditions = conditions.filter((item) => item.type !== key);
        }
        dispatch(
          checkoutRuleSlice.actions.handleChange({
            key: conditionType,
            value: conditions as IItemCondition[] | ILocationCondition[],
          }),
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, JSON.stringify(state)],
  );

  const handleSetError = useCallback(
    (field: ICheckoutRuleErrorField, msg: string) => {
      dispatch(
        checkoutRuleSlice.actions.handleSetError({
          value: { field, msg },
        }),
      );
    },
    [dispatch],
  );

  const handleShowRemoveCondition =
    (conditionType: 'checkoutItemConditions' | 'checkoutLocationConditions') =>
    (condition: IItemCondition['type'] | ILocationCondition['type']) => {
      setSelectedItemToRemove({ conditionType, condition });
      setIsOpenModalConfirmRemoveCondition(true);
    };

  const handleConfirmRemoveCondition = () => {
    const { conditionType, condition } = selectedItemToRemove;
    if (condition) {
      let field = condition as ICheckoutRuleErrorField;
      switch (condition) {
        case ItemConditionEnum.ExcludeProduct:
        case ItemConditionEnum.IncludeProduct:
          field = 'product';
          break;
        case ItemConditionEnum.ExcludeCollection:
        case ItemConditionEnum.IncludeCollection:
          field = 'collection';
          break;
        case ItemConditionEnum.ExcludeProductTag:
        case ItemConditionEnum.IncludeProductTag:
          field = 'productTags';
          break;
        case ItemConditionEnum.ExcludeSKU:
        case ItemConditionEnum.IncludeSKU:
          field = 'skus';
          break;
        case ItemConditionEnum.ExcludeVendor:
        case ItemConditionEnum.IncludeVendor:
          field = 'vendor';
          break;
        case ItemConditionEnum.Exception:
          field = 'exceptionProduct';
          break;
        default:
          break;
      }

      handleChangeCondition(conditionType)(condition)(null);
      handleSetError(field as ICheckoutRuleErrorField, '');
      setTrackRemove((prev) => !prev);
      setIsOpenModalConfirmRemoveCondition(false);
    }
  };

  return (
    <div>
      <ConditionBanner
        isVisible={isShowBannerConditionCheckout}
        onDismiss={() => dispatch(sessionSlice.actions.handleBanner({ key: 'isShowBannerConditionCheckout', value: false }))}
        isAllCondition={!state.checkoutItemConditions.length && !state.checkoutLocationConditions.length}
      />
      <div className={isShowBannerConditionCheckout ? 'mt-24' : ''}>
        <ExcludeRuleCondition
          onAction={() => {
            setIsOpenModalItemCondition(true);
          }}
          data={state.checkoutItemConditions}
          onChange={handleChangeCondition('checkoutItemConditions')}
          type={isInCaseInclude ? AppliedItemTypes.Include : isInCaseExclude ? AppliedItemTypes.Exclude : AppliedItemTypes.All}
          onRemove={handleShowRemoveCondition('checkoutItemConditions')}
          trackRemove={trackRemove}
          isInCheckout
          typeRelationOfItem={state.typeRelationOfItemCondition}
          dataLocationConditions={state.checkoutLocationConditions}
          onAddLocationCondition={handleChangeCondition('checkoutLocationConditions')}
          onRemoveLocationCondition={handleShowRemoveCondition('checkoutLocationConditions')}
        />
      </div>
      <ModalItemCondition
        isOpenModal={isOpenModalItemCondition}
        setIsOpenModal={setIsOpenModalItemCondition}
        onAdd={handleChangeCondition('checkoutItemConditions')}
        data={state.checkoutItemConditions}
        type={isInCaseInclude ? AppliedItemTypes.Include : isInCaseExclude ? AppliedItemTypes.Exclude : AppliedItemTypes.All}
        isInCheckout
        dataLocationConditions={state.checkoutLocationConditions}
        onAddLocationCondition={handleChangeCondition('checkoutLocationConditions')}
      />
      <BaseModal
        open={isOpenModalConfirmRemoveCondition}
        title={'Delete this display condition?'}
        onClose={() => setIsOpenModalConfirmRemoveCondition(false)}
        footer={
          <div className="content-right" style={{ width: isMobile ? 'calc(100vw - 3rem)' : '588px' }}>
            <Button onClick={() => setIsOpenModalConfirmRemoveCondition(false)}>Cancel</Button>
            <div className="ml-8">
              <Button tone="critical" variant="primary" onClick={handleConfirmRemoveCondition}>
                Delete
              </Button>
            </div>
          </div>
        }
      >
        <RegularText>This action cannot be undone</RegularText>
      </BaseModal>
    </div>
  );
};

export default Step2;
